import React, { useMemo } from "react";
import { getSizedImage } from "@/helpers/utils";
import { ProductCardListCollection } from "@/features/product/ProductCardListCollection";
import { productSelect, variantSelect } from "@/store";
import { PageModule } from "@/components/PageModule";
import { useMediaQuery } from "@mui/material";
import { theme } from "@/theme";
import { storefrontApi } from "@/services/storefront";
import { ProductCardProduct } from "@/features/product/ProductCard";
import { ShoppableProductCardProduct } from "@/features/product/ShoppableProductCard";
import { ProductSliderProps } from "./ProductSlider";
import { groupBy } from "@/helpers/utils";

export const ProductSliderCollection = ({
  collectionHandle,
  shoppable = true,
  numProducts = 50,
  layout = "carousel",
  condensedView = false,
  className,
  firstSlider = false,
  productHandles,
}: ProductSliderProps) => {
  const skeletonCards = useMemo<ProductCardProduct[]>(() => {
    return Array(numProducts).fill({
      imageHeight: 440,
      imageWidth: 549,
      featuredImage: true,
      featuredText: !condensedView,
      type: true,
      ratings: true,
      url: !condensedView,
      title: true,
      badge: true,
      price: true,
      size: true,
    });
  }, [numProducts, condensedView]);

  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  const collection = storefrontApi.useCollection(collectionHandle, {
    limit: numProducts,
  });

  const productCards = useMemo<
    ShoppableProductCardProduct[] | undefined
  >(() => {
    if (collection.data === undefined) {
      return undefined;
    }

    let products = collection.data.all.filter((x) => !x.hidden);

    // If productHandles is provided, filter to only matching handles
    if (productHandles && productHandles.length > 0) {
      const handleSet = new Set(productHandles);
      products = products.filter((p) => handleSet.has(p.handle));
    }

    const grouped = groupBy(products, (p) => p.productGroup || p.id.toString());
    const selectedProducts = Array.from(grouped.values()).map(
      (group) => group[0]
    );

    return selectedProducts.map((product, i) => {
      const variant = productSelect.defaultVariant(product);
      const featuredText =
        product.featuredQuote && !condensedView
          ? `“${product.featuredQuote}”`
          : undefined;

      // Get the group for this product
      const groupKey = product.productGroup || product.id.toString();
      const groupProducts = grouped.get(groupKey) || [];

      return {
        id: variant.id,
        featuredImage: {
          src: getSizedImage(productSelect.featuredImage(product).url, "x400"),
          height: 440,
          width: 549,
          loading: firstSlider && i === 0 ? "eager" : "lazy",
        },
        badge: product.badge,
        featuredText,
        type: product.type,
        url: productSelect.url(product),
        title: product.title,
        size: variantSelect.sizeFormat(variant),
        compareAtPrice: variant.compareAtPrice,
        price: shoppable
          ? variant.price
          : variantSelect.lowestPrice(variant, product),
        product,
        variant,
        collectionProducts: groupProducts, // Pass the group products
      };
    });
  }, [collection.data, condensedView, shoppable, firstSlider, productHandles]);

  const data =
    productCards && shoppable
      ? ({
          shoppable: true,
          products: productCards,
        } as const)
      : ({
          shoppable: false,
          products: productCards ?? skeletonCards,
        } as const);

  return (
    <PageModule
      className={`${
        isSmallScreen && layout === "carousel" ? "px-0" : ""
      } ${className}`}
    >
      <ProductCardListCollection layout={layout} {...data} />
    </PageModule>
  );
};
